// apps/dashboard/src/routes/targets/new.tsx
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@clerk/react";
import { createTarget, type CreateTargetInput } from "@/api/client";
import { track } from "@/lib/analytics";
import { PageChrome } from "@/components/chrome/PageChrome";
import { useTargetDraft } from "@/lib/target-draft-store";
import { isValidServerUrl, stripScheme } from "@/lib/url";
import { Wizard } from "@/components/targets/wizard/Wizard";
import { WizardFooter } from "@/components/targets/wizard/WizardFooter";
import { IdentifyStep, IdentifyHelper } from "@/components/targets/wizard/IdentifyStep";
import {
  CredentialsStep,
  CredentialsHelper,
} from "@/components/targets/wizard/CredentialsStep";

export const Route = createFileRoute("/targets/new")({
  component: NewTargetWizard,
});

function NewTargetWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const organization = useOrganization();

  const draft = useTargetDraft((s) => s.draft);
  const startDraftFromTarget = useTargetDraft((s) => s.startDraftFromTarget);
  const clearDraft = useTargetDraft((s) => s.clearDraft);

  // Local copy of step-1 inputs — only persisted (via createTarget) when
  // the user clicks Continue on the Target details step.
  const [displayName, setDisplayName] = useState("");
  const [url, setUrl] = useState("");

  const createMutation = useMutation({
    mutationFn: (input: CreateTargetInput) => createTarget(input),
    onSuccess: (target) => {
      // Prime the query cache so any downstream consumer renders the
      // freshly created target immediately, then seed the draft with
      // UI-cursor fields. Ownership verification is deferred to the
      // target settings page; the wizard no longer blocks on DNS.
      queryClient.setQueryData(["target", target.id], target);
      track({ event: "server_created" });
      startDraftFromTarget({
        targetId: target.id,
        displayName: target.displayName,
        url: target.url,
      });
      void queryClient.invalidateQueries({ queryKey: ["targets"] });
    },
  });

  const step: 1 | 2 = draft ? draft.currentStep : 1;

  const handleBack = () => {
    // Step 2 → 1 means abandoning the just-created target; discard the
    // draft so the user starts fresh.
    if (step === 2) clearDraft();
  };

  const handleContinue = () => {
    if (step === 1) {
      createMutation.mutate({ displayName, url });
      return;
    }
  };

  const finishToTarget = () => {
    if (!draft) return;
    const id = draft.targetId;
    clearDraft();
    void queryClient.invalidateQueries({ queryKey: ["targets"] });
    void navigate({ to: "/targets/$targetId", params: { targetId: id } });
  };

  // Block advancing on a malformed URL — `new URL()` accepts bare labels
  // like "acme", so we validate for a real host before the create call.
  const urlInvalid = url.trim().length > 0 && !isValidServerUrl(url);
  const continueDisabled =
    step === 1 && (!displayName.trim() || !url.trim() || urlInvalid);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageChrome
        crumbs={[
          { label: organization.organization?.name || "" },
          { label: "Servers", to: "/targets" },
          { label: "New" },
        ]}
        title="Add an MCP server"
      />
      <div className="flex flex-1 flex-col overflow-y-auto px-4 sm:px-7 py-8">
        <div className="mx-auto w-full max-w-275">
          <header className="mb-7 flex flex-col gap-1.5">
            <h2 className="m-0 text-page-title font-semibold leading-[1.15] tracking-[-0.018em] text-fg-1">
              Add an MCP server
            </h2>
            <p className="m-0 max-w-[64ch] text-body leading-relaxed text-fg-2">
              Two steps. Name your server, then add the credential Gated
              should scan with — or skip and do it later. Ownership
              verification, when needed, lives on the server's settings
              page.
            </p>
          </header>

          <Wizard
            step={step}
            helper={helperFor(step)}
            footer={
              <WizardFooter
                step={step}
                onBack={handleBack}
                onContinue={handleContinue}
                onFinish={finishToTarget}
                continueDisabled={continueDisabled}
                continuePending={createMutation.isPending}
              />
            }
          >
            {step === 1 ? (
              <IdentifyStep
                displayName={displayName}
                url={url}
                onDisplayNameChange={setDisplayName}
                onUrlChange={setUrl}
                errorMessage={
                  urlInvalid
                    ? "Enter a valid URL with a hostname, e.g. https://mcp.acme.com"
                    : errorMessageFor(createMutation.error)
                }
              />
            ) : null}
            {step === 2 && draft ? (
              <CredentialsStep
                targetId={draft.targetId}
                host={stripScheme(draft.url)}
              />
            ) : null}
          </Wizard>
        </div>
      </div>
    </div>
  );
}

function helperFor(step: 1 | 2) {
  if (step === 1) return <IdentifyHelper />;
  return <CredentialsHelper />;
}

function errorMessageFor(err: unknown): string | undefined {
  if (err instanceof Error) {
    if (err.message.includes("target_url_exists")) {
      return "A target with this URL already exists in your org.";
    }
    return err.message;
  }
  return undefined;
}
